import React from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  Pagination,
  Paper,
  Stack,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedListItemText as ListItemText, LocalizedMenuItem as MenuItem, LocalizedTextField as TextField } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { translateInline } from '../i18n/inlineTranslations';
import { normalizeLanguage } from '../i18n/languages';
import { resolvePdfLanguage, useUserPreferences } from '../contexts/UserPreferencesContext';
import {
  Add,
  Assessment,
  Dashboard,
  Description,
  Download,
  ContactPage,
  EventRepeat,
  Group,
  Healing,
  Lock as LockIcon,
  Notes,
  Payment,
  People,
  Psychology,
  Search,
  Settings,
  Visibility,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { studioApi as axios } from '../api/studioClient';
import { hasPremiumSubscription } from '../utils/subscription';
import { formatDisplayDate } from '../utils/dateDisplay';

const cmsHi: Record<string, string> = {
  'Client Management': 'क्लाइंट मैनेजमेंट',
  Dashboard: 'डैशबोर्ड',
  Clients: 'क्लाइंट्स',
  Consultations: 'परामर्श',
  Notes: 'नोट्स',
  'Personal Notes': 'व्यक्तिगत नोट्स',
  'Notes to Clients': 'क्लाइंट के लिए नोट्स',
  'Follow-ups': 'फॉलो-अप',
  Remedies: 'उपाय',
  Predictions: 'भविष्यवाणी',
  Planner: 'प्लानर',
  Templates: 'टेम्पलेट्स',
  Analytics: 'विश्लेषण',
  Payments: 'भुगतान',
  Settings: 'सेटिंग्स',
  Records: 'रिकॉर्ड',
  Relations: 'संबंध',
  Relation: 'संबंध',
  'Add Client': 'क्लाइंट जोड़ें',
  'Add Consultation': 'परामर्श जोड़ें',
  'Add Personal Note': 'व्यक्तिगत नोट जोड़ें',
  'Add Note to Client': 'क्लाइंट के लिए नोट जोड़ें',
  'Add Follow-up': 'फॉलो-अप जोड़ें',
  'Assign Remedy': 'उपाय असाइन करें',
  'Add Prediction': 'भविष्यवाणी जोड़ें',
  'Add Payment': 'भुगतान जोड़ें',
  'Assign Chart': 'कुंडली असाइन करें',
  'Add Relation': 'संबंध जोड़ें',
  'CMS Action': 'CMS कार्य',
  Client: 'क्लाइंट',
  Chart: 'कुंडली',
  'Related Client': 'संबंधित क्लाइंट',
  'Full Name': 'पूरा नाम',
  'Phone Number': 'फोन नंबर',
  'WhatsApp Number': 'व्हाट्सऐप नंबर',
  Email: 'ईमेल',
  'Birth Place': 'जन्म स्थान',
  'Current City': 'वर्तमान शहर',
  'Main Topic': 'मुख्य विषय',
  Status: 'स्थिति',
  Source: 'स्रोत',
  Tags: 'टैग',
  'Internal Note': 'आंतरिक नोट',
  Purpose: 'उद्देश्य',
  'Relation Type': 'संबंध प्रकार',
  Title: 'शीर्षक',
  Topic: 'विषय',
  Date: 'तिथि',
  Time: 'समय',
  Amount: 'राशि',
  Mode: 'माध्यम',
  'Payment Status': 'भुगतान स्थिति',
  'Note / Summary': 'नोट / सारांश',
  Close: 'बंद करें',
  Cancel: 'रद्द करें',
  Save: 'सेव करें',
  Saving: 'सेव हो रहा है...',
  'Save Changes': 'बदलाव सेव करें',
  'Edit Client': 'क्लाइंट संपादित करें',
  'Client Chart': 'क्लाइंट कुंडली',
  'Comma separated': 'कॉमा से अलग करें',
  'Search by name, phone, email, topic, or tag': 'नाम, फोन, ईमेल, विषय या टैग से खोजें',
  'Search clients, phone, topic': 'क्लाइंट, फोन, विषय खोजें',
  'Search your saved charts by chart name': 'अपनी सेव कुंडली नाम से खोजें',
  'Linked Chart': 'लिंक कुंडली',
  'Select linked chart': 'लिंक कुंडली चुनें',
  'Link a chart to this client before adding records.': 'रिकॉर्ड जोड़ने से पहले इस क्लाइंट से कुंडली लिंक करें।',
  'Connect this client with another saved client profile': 'इस क्लाइंट को दूसरे सेव क्लाइंट प्रोफाइल से जोड़ें',
  'CMS for astrologer client work': 'ज्योतिषी क्लाइंट कार्य के लिए CMS',
  'Premium Pro unlocks Client Management': 'Premium Pro से Client Management अनलॉक करें',
  'Upgrade to manage client profiles, consultations, notes, follow-ups, remedies, predictions, analytics and payment tracking in one workspace.': 'क्लाइंट प्रोफाइल, परामर्श, नोट्स, फॉलो-अप, उपाय, भविष्यवाणी, एनालिटिक्स और पेमेंट ट्रैकिंग को एक जगह मैनेज करने के लिए अपग्रेड करें।',
  'Upgrade to Premium Pro': 'Premium Pro में अपग्रेड करें',
  'CMS modules included': 'CMS मॉड्यूल शामिल',
  'Please login to use Client Management. CMS data is stored per account.': 'Client Management इस्तेमाल करने के लिए लॉगिन करें। CMS डेटा हर खाते के अनुसार सेव होता है।',
  Login: 'लॉगिन',
  'Loading live CMS data...': 'लाइव CMS डेटा लोड हो रहा है...',
  'Quick Actions': 'त्वरित कार्य',
  'Client Records': 'क्लाइंट रिकॉर्ड',
  'Client Record': 'क्लाइंट रिकॉर्ड',
  'Client Profile': 'क्लाइंट प्रोफाइल',
  'Back to Clients': 'क्लाइंट्स पर वापस जाएं',
  View: 'देखें',
  Edit: 'संपादित करें',
  Delete: 'हटाएं',
  Note: 'नोट',
  Archive: 'आर्काइव',
  'Download PDF': 'PDF डाउनलोड करें',
  'Delete this record?': 'यह रिकॉर्ड हटाएं?',
  'Unable to edit this CMS record.': 'यह CMS रिकॉर्ड संपादित नहीं हो सका।',
  'Unable to update CMS record.': 'CMS रिकॉर्ड अपडेट नहीं हो सका।',
  'Unable to delete CMS record.': 'CMS रिकॉर्ड हट नहीं सका।',
  'No contact added': 'कोई संपर्क नहीं जोड़ा गया',
  'No topic': 'कोई विषय नहीं',
  'No clients found yet. Add your first real client to start filling the CMS dashboard.': 'अभी कोई क्लाइंट नहीं मिला। CMS डैशबोर्ड शुरू करने के लिए पहला असली क्लाइंट जोड़ें।',
  'Last Consultation': 'पिछला परामर्श',
  'Next Follow-up': 'अगला फॉलो-अप',
  'Date of Birth': 'जन्म तिथि',
  'Time of Birth': 'जन्म समय',
  'Client Details': 'क्लाइंट विवरण',
  'No connected profiles yet.': 'अभी कोई जुड़ा हुआ प्रोफाइल नहीं है।',
  'Select a client record to view details.': 'विवरण देखने के लिए क्लाइंट रिकॉर्ड चुनें।',
  Record: 'रिकॉर्ड',
  'Consultation Summary': 'परामर्श सारांश',
  'Client Note': 'क्लाइंट नोट',
  'Start a consultation record with summary, topic, mode and payment fields.': 'सारांश, विषय, माध्यम और भुगतान fields के साथ परामर्श रिकॉर्ड शुरू करें।',
  'Start a private note attached to a client profile.': 'क्लाइंट प्रोफाइल से जुड़ा निजी नोट शुरू करें।',
  'Start a pending follow-up with due date and topic.': 'due date और विषय के साथ pending follow-up शुरू करें।',
  'Start an active remedy record for client guidance.': 'क्लाइंट guidance के लिए active remedy record शुरू करें।',
  'Start an open prediction record with notes and follow-up date.': 'notes और follow-up date के साथ open prediction record शुरू करें।',
  Statuses: 'स्थितियां',
  Topics: 'विषय',
  Sources: 'स्रोत',
  'No records found for the current filters.': 'मौजूदा फिल्टर में कोई रिकॉर्ड नहीं मिला।',
  'Total Clients': 'कुल क्लाइंट्स',
  'New Clients This Month': 'इस महीने नए क्लाइंट्स',
  'Consultations This Month': 'इस महीने परामर्श',
  "Today's Consultations": 'आज के परामर्श',
  'Consultations scheduled for today from real client records.': 'असली क्लाइंट रिकॉर्ड से आज के लिए तय परामर्श।',
  'No consultations scheduled for today.': 'आज के लिए कोई परामर्श तय नहीं है।',
  'Pending Follow-ups': 'फॉलो-अप लंबित',
  'Active Remedies': 'सक्रिय उपाय',
  'Open Predictions': 'खुली भविष्यवाणी',
  'Pending Payments': 'लंबित भुगतान',
  'See client activity, recent work, follow-ups, predictions, remedies and pending payments in one place.': 'क्लाइंट गतिविधि, हाल का काम, फॉलो-अप, भविष्यवाणी, उपाय और लंबित भुगतान एक जगह देखें।',
  'Manage astrologer client records with birth details, topics, tags, status and archive/restore actions.': 'जन्म विवरण, विषय, टैग, स्थिति और आर्काइव/रिस्टोर कार्यों के साथ ज्योतिषी क्लाइंट रिकॉर्ड संभालें।',
  'Store session details, linked charts, topics, observations, predictions, remedies and follow-up dates.': 'सेशन विवरण, लिंक कुंडली, विषय, अवलोकन, भविष्यवाणी, उपाय और फॉलो-अप तिथियां सेव करें।',
  'Keep private consultation notes and chart observations attached to client profiles.': 'निजी परामर्श नोट्स और कुंडली अवलोकन क्लाइंट प्रोफाइल से जुड़े रखें।',
  'Track today and upcoming follow-ups inside Client Management without push, SMS, email or WhatsApp alerts.': 'पुश, SMS, ईमेल या WhatsApp अलर्ट के बिना आज और आने वाले फॉलो-अप Client Management में ट्रैक करें।',
  'Assign remedies, mark active guidance and keep remedy history connected to the client timeline.': 'उपाय असाइन करें, सक्रिय मार्गदर्शन मार्क करें और उपाय इतिहास को क्लाइंट टाइमलाइन से जोड़कर रखें।',
  'Track open predictions, dates, confidence notes and outcome updates for each client.': 'हर क्लाइंट के लिए खुली भविष्यवाणी, तिथियां, भरोसा नोट्स और परिणाम अपडेट ट्रैक करें।',
  'Plan consultations, follow-ups and payment dates in one working view.': 'परामर्श, फॉलो-अप और भुगतान तिथियां एक कामकाजी व्यू में प्लान करें।',
  'Prepare reusable consultation, remedy and prediction templates.': 'दोबारा इस्तेमाल होने वाले परामर्श, उपाय और भविष्यवाणी टेम्पलेट तैयार करें।',
  'Review client activity, consultation volume and pending work.': 'क्लाइंट गतिविधि, परामर्श वॉल्यूम और लंबित काम देखें।',
  'Track consultation amounts and payment status without plan or pricing rules.': 'प्लान या pricing rules के बिना परामर्श राशि और भुगतान स्थिति ट्रैक करें।',
  'Configure client sources, statuses, topics, tags and internal CMS preferences.': 'क्लाइंट स्रोत, स्थितियां, विषय, टैग और आंतरिक CMS preferences सेट करें।',
  'Showing saved records connected to client records.': 'क्लाइंट रिकॉर्ड से जुड़े सेव रिकॉर्ड दिखाए जा रहे हैं।',
  'No Status': 'कोई स्थिति नहीं',
  'No Mode': 'कोई माध्यम नहीं',
  'No Payment Status': 'कोई भुगतान स्थिति नहीं',
  'No Topic': 'कोई विषय नहीं',
  'No Source': 'कोई स्रोत नहीं',
  'All Statuses': 'सभी स्थितियां',
  'All Topics': 'सभी विषय',
  'No clients found': 'कोई क्लाइंट नहीं मिला',
  'Type to search clients': 'क्लाइंट खोजने के लिए टाइप करें',
  'No charts found': 'कोई कुंडली नहीं मिली',
  'Type to search saved charts': 'सेव कुंडली खोजने के लिए टाइप करें',
  'Type to search related client': 'संबंधित क्लाइंट खोजने के लिए टाइप करें',
  Open: 'खुला',
  Pending: 'लंबित',
  Scheduled: 'निर्धारित',
  Completed: 'पूर्ण',
  'Follow-up Pending': 'फॉलो-अप लंबित',
  Cancelled: 'रद्द',
  Closed: 'बंद',
  'No-show': 'नहीं आए',
  'Not Applicable': 'लागू नहीं',
  Paid: 'भुगतान हो गया',
  'Partially Paid': 'आंशिक भुगतान',
  Refunded: 'रिफंड',
  Phone: 'फोन',
  'WhatsApp Call': 'व्हाट्सऐप कॉल',
  'Video Call': 'वीडियो कॉल',
  'In-person': 'सामने मिलकर',
  Chat: 'चैट',
  Other: 'अन्य',
  New: 'नया',
  VIP: 'VIP',
  Inactive: 'निष्क्रिय',
  'Consultation Completed': 'परामर्श पूर्ण',
  WhatsApp: 'व्हाट्सऐप',
  Instagram: 'इंस्टाग्राम',
  YouTube: 'यूट्यूब',
  Referral: 'रेफरल',
  Website: 'वेबसाइट',
  'Walk-in': 'वॉक-इन',
  'Repeat Client': 'दोबारा आए क्लाइंट',
  'Family Referral': 'परिवार रेफरल',
  Marriage: 'विवाह',
  Career: 'करियर',
  Business: 'व्यवसाय',
  Health: 'स्वास्थ्य',
  Finance: 'वित्त',
  Education: 'शिक्षा',
  Property: 'संपत्ति',
  Children: 'संतान',
  'Foreign Settlement': 'विदेश सेटलमेंट',
  Legal: 'कानूनी',
  Remedy: 'उपाय',
  General: 'सामान्य',
  Active: 'सक्रिय',
  Past: 'पूर्व',
  Potential: 'संभावित',
  Archived: 'आर्काइव',
  Spouse: 'जीवनसाथी',
  Husband: 'पति',
  Wife: 'पत्नी',
  Child: 'बच्चा',
  Son: 'बेटा',
  Daughter: 'बेटी',
  Father: 'पिता',
  Mother: 'माता',
  Sibling: 'भाई-बहन',
  Brother: 'भाई',
  Sister: 'बहन',
  'Marriage Prospect': 'विवाह प्रस्ताव',
  Fiance: 'मंगेतर',
  'Business Partner': 'व्यवसाय साझेदार',
  Friend: 'मित्र',
  Relative: 'रिश्तेदार',
};

const cmsSections = [
  { key: 'dashboard', label: 'Dashboard', path: '/cms/dashboard', icon: <Dashboard /> },
  { key: 'clients', label: 'Clients', path: '/cms/clients', icon: <People /> },
  { key: 'consultations', label: 'Consultations', path: '/cms/consultations', icon: <Group /> },
  { key: 'notes', label: 'Personal Notes', path: '/cms/notes', icon: <Notes /> },
  { key: 'clientnotes', label: 'Notes to Clients', path: '/cms/clientnotes', icon: <Description /> },
  { key: 'followups', label: 'Follow-ups', path: '/cms/followups', icon: <EventRepeat /> },
  { key: 'remedies', label: 'Remedies', path: '/cms/remedies', icon: <Healing /> },
  { key: 'predictions', label: 'Predictions', path: '/cms/predictions', icon: <Psychology /> },
  { key: 'templates', label: 'Templates', path: '/cms/templates', icon: <Description /> },
  { key: 'analytics', label: 'Analytics', path: '/cms/analytics', icon: <Assessment /> },
  { key: 'payments', label: 'Payments', path: '/cms/payments', icon: <Payment /> },
  { key: 'settings', label: 'Settings', path: '/cms/settings', icon: <Settings /> },
] as const;

type CmsSectionKey = typeof cmsSections[number]['key'];

type CmsClient = {
  _id: string;
  id?: string;
  fullName: string;
  phoneNumber?: string;
  whatsappNumber?: string;
  email?: string;
  birthPlace?: string;
  currentCity?: string;
  dateOfBirth?: string;
  timeOfBirth?: string;
  gender?: string;
  languagePreference?: string;
  mainTopic?: string;
  clientStatus?: string;
  clientSource?: string;
  tags?: string[];
  internalNote?: string;
  lastConsultationAt?: string | null;
  nextFollowUpAt?: string | null;
  counts?: Record<string, number>;
  charts?: CmsModuleRecord[];
  consultations?: CmsModuleRecord[];
  notes?: CmsModuleRecord[];
  clientNotes?: CmsModuleRecord[];
  followUps?: CmsModuleRecord[];
  remedies?: CmsModuleRecord[];
  predictions?: CmsModuleRecord[];
  relations?: CmsRelation[];
  payments?: CmsModuleRecord[];
};

type CmsRelation = {
  _id?: string;
  id?: string;
  relatedClientId?: string;
  relatedClientName?: string;
  linkedChartId?: string;
  relationType?: string;
  status?: string;
  note?: string;
  createdAt?: string;
};

type CmsModuleRecord = {
  _id?: string;
  id?: string;
  type?: string;
  typeLabel?: string;
  chartId?: string;
  chartName?: string;
  chartType?: string;
  purpose?: string;
  tags?: string[];
  notes?: string;
  archivedAt?: string;
  title?: string;
  clientId?: string;
  clientName?: string;
  clientPhone?: string;
  topic?: string;
  status?: string;
  amount?: number;
  amountCharged?: number;
  paymentStatus?: string;
  mode?: string;
  linkedChartId?: string;
  body?: string;
  note?: string;
  summary?: string;
  dueDate?: string;
  consultationDate?: string;
  consultationTime?: string;
  dueTime?: string;
  recordTime?: string;
  recordDate?: string;
  createdAt?: string;
};

type CmsChartOption = {
  _id: string;
  chartType?: string;
  birthDetails?: {
    name?: string;
    dateOfBirth?: string;
    placeOfBirth?: {
      city?: string;
      state?: string;
      country?: string;
    };
  };
  createdAt?: string;
};

type DashboardData = {
  stats: {
    totalClients: number;
    newClientsThisMonth: number;
    consultationsThisMonth: number;
    pendingFollowUps: number;
    activeRemedies: number;
    openPredictions: number;
    pendingPaymentsAmount: number;
    pendingPayments: number;
  };
  recentClients: CmsClient[];
  todayFollowUps: any[];
  todayConsultations: any[];
  todayPlanner: any[];
  upcomingFollowUps: any[];
  recentConsultations: any[];
  recentNotes: any[];
  openPredictions: any[];
  pendingPayments: any[];
  quickActions: string[];
};

type PaginationData = {
  page: number;
  limit: number;
  total: number;
  pages: number;
};

const emptyDashboard: DashboardData = {
  stats: {
    totalClients: 0,
    newClientsThisMonth: 0,
    consultationsThisMonth: 0,
    pendingFollowUps: 0,
    activeRemedies: 0,
    openPredictions: 0,
    pendingPaymentsAmount: 0,
    pendingPayments: 0,
  },
  recentClients: [],
  todayFollowUps: [],
  todayConsultations: [],
  todayPlanner: [],
  upcomingFollowUps: [],
  recentConsultations: [],
  recentNotes: [],
  openPredictions: [],
  pendingPayments: [],
  quickActions: ['Add Client', 'Add Consultation', 'Add Personal Note', 'Add Note to Client', 'Add Follow-up', 'Assign Remedy', 'Add Prediction', 'Add Payment', 'Assign Chart'],
};

const sectionSummaries: Record<CmsSectionKey, string> = {
  dashboard: 'See client activity, recent work, follow-ups, predictions, remedies and pending payments in one place.',
  clients: 'Manage astrologer client records with birth details, topics, tags, status and archive/restore actions.',
  consultations: 'Store session details, linked charts, topics, observations, predictions, remedies and follow-up dates.',
  notes: 'Keep private consultation notes and chart observations attached to client profiles.',
  clientnotes: 'Write chart-linked notes that are added at the end of the client chart PDF.',
  followups: 'Track today and upcoming follow-ups inside Client Management without push, SMS, email or WhatsApp alerts.',
  remedies: 'Assign remedies, mark active guidance and keep remedy history connected to the client timeline.',
  predictions: 'Track open predictions, dates, confidence notes and outcome updates for each client.',
  templates: 'Prepare reusable consultation, remedy and prediction templates.',
  analytics: 'Review client activity, consultation volume and pending work.',
  payments: 'Track consultation amounts and payment status without plan or pricing rules.',
  settings: 'Configure client sources, statuses, topics, tags and internal CMS preferences.',
};

const actionConfig: Record<string, { type: string; title: string; needsClient: boolean }> = {
  'Add Client': { type: 'client', title: 'Add Client', needsClient: false },
  'Add Consultation': { type: 'consultations', title: 'Add Consultation', needsClient: true },
  'Add Personal Note': { type: 'notes', title: 'Add Personal Note', needsClient: true },
  'Add Note to Client': { type: 'clientnotes', title: 'Add Note to Client', needsClient: true },
  'Add Follow-up': { type: 'followups', title: 'Add Follow-up', needsClient: true },
  'Assign Remedy': { type: 'remedies', title: 'Assign Remedy', needsClient: true },
  'Add Prediction': { type: 'predictions', title: 'Add Prediction', needsClient: true },
  'Add Relation': { type: 'relations', title: 'Add Relation', needsClient: true },
  'Add Payment': { type: 'payments', title: 'Add Payment', needsClient: true },
  'Assign Chart': { type: 'charts', title: 'Assign Chart', needsClient: true },
};

const moduleActions: Partial<Record<CmsSectionKey, string>> = {
  consultations: 'Add Consultation',
  notes: 'Add Personal Note',
  clientnotes: 'Add Note to Client',
  followups: 'Add Follow-up',
  remedies: 'Assign Remedy',
  predictions: 'Add Prediction',
  payments: 'Add Payment',
};

const recordActionByType: Record<string, string> = {
  consultations: 'Add Consultation',
  notes: 'Add Personal Note',
  clientnotes: 'Add Note to Client',
  followups: 'Add Follow-up',
  remedies: 'Assign Remedy',
  predictions: 'Add Prediction',
  payments: 'Add Payment',
};

const clientRecordGroups = [
  { key: 'consultations', label: 'Consultations', recordsKey: 'consultations', typeLabel: 'Consultation' },
  { key: 'notes', label: 'Personal Notes', recordsKey: 'notes', typeLabel: 'Personal Note' },
  { key: 'clientnotes', label: 'Notes to Clients', recordsKey: 'clientNotes', typeLabel: 'Note to Client' },
  { key: 'followups', label: 'Follow-ups', recordsKey: 'followUps', typeLabel: 'Follow-up' },
  { key: 'remedies', label: 'Remedies', recordsKey: 'remedies', typeLabel: 'Remedy' },
  { key: 'predictions', label: 'Predictions', recordsKey: 'predictions', typeLabel: 'Prediction' },
  { key: 'payments', label: 'Payments', recordsKey: 'payments', typeLabel: 'Payment' },
] as const;

const recordModuleKeys = new Set<CmsSectionKey>([
  'consultations',
  'notes',
  'clientnotes',
  'followups',
  'remedies',
  'predictions',
  'payments',
]);

type TemplateAction = {
  title: string;
  action: string;
  helper: string;
  form: Record<string, string>;
};

const templateActions: TemplateAction[] = [
  {
    title: 'Consultation Summary',
    action: 'Add Consultation',
    helper: 'Start a consultation record with summary, topic, mode and payment fields.',
    form: { status: 'Scheduled', paymentStatus: 'Not Applicable' },
  },
  {
    title: 'Personal Note',
    action: 'Add Personal Note',
    helper: 'Start a private note attached to a client profile.',
    form: { title: 'Personal Note' },
  },
  {
    title: 'Follow-up',
    action: 'Add Follow-up',
    helper: 'Start a pending follow-up with due date and topic.',
    form: { title: 'Follow-up', status: 'Pending' },
  },
  {
    title: 'Remedy',
    action: 'Assign Remedy',
    helper: 'Start an active remedy record for client guidance.',
    form: { title: 'Remedy', topic: 'Remedy', status: 'Open' },
  },
  {
    title: 'Prediction',
    action: 'Add Prediction',
    helper: 'Start an open prediction record with notes and follow-up date.',
    form: { title: 'Prediction', status: 'Open' },
  },
];

const clientTopics = ['Marriage', 'Career', 'Business', 'Health', 'Finance', 'Education', 'Property', 'Children', 'Foreign Settlement', 'Legal', 'Remedy', 'General'];
const clientStatuses = ['New', 'Active', 'Consultation Completed', 'Follow-up Pending', 'VIP', 'Inactive'];
const clientSources = ['WhatsApp', 'Instagram', 'YouTube', 'Referral', 'Website', 'Walk-in', 'Repeat Client', 'Family Referral', 'Other'];
const relationTypes = ['Spouse', 'Husband', 'Wife', 'Child', 'Son', 'Daughter', 'Father', 'Mother', 'Sibling', 'Brother', 'Sister', 'Marriage Prospect', 'Fiance', 'Business Partner', 'Friend', 'Relative', 'Other'];
const relationStatuses = ['Active', 'Past', 'Potential', 'Archived'];
const recordStatuses = ['Open', 'Pending', 'Scheduled', 'Completed', 'Follow-up Pending', 'Cancelled', 'Closed'];
const consultationStatuses = ['Scheduled', 'Completed', 'Follow-up Pending', 'Cancelled', 'No-show'];
const followUpStatuses = ['Pending', 'Completed', 'Cancelled'];
const paymentStatuses = ['Not Applicable', 'Pending', 'Paid', 'Partially Paid', 'Refunded'];
const consultationModes = ['Phone', 'WhatsApp Call', 'Video Call', 'In-person', 'Chat', 'Email', 'Other'];

function getActiveSection(pathname: string): CmsSectionKey {
  const [, cms, section] = pathname.split('/');
  if (cms !== 'cms') return 'dashboard';
  return (cmsSections.some((item) => item.key === section) ? section : 'dashboard') as CmsSectionKey;
}

const formatDate = (value?: string | null) => {
  return formatDisplayDate(value, { fallback: '-' });
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const getDefaultCmsDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultCmsTime = () => {
  const now = new Date();
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const getDefaultCmsFormValues = (actionType?: string): Record<string, string> => {
  if (!actionType) return {};
  const defaultDate = getDefaultCmsDate();
  const defaultTime = getDefaultCmsTime();

  if (['consultations', 'followups', 'remedies', 'predictions', 'payments'].includes(actionType)) {
    return { date: defaultDate, consultationTime: defaultTime };
  }

  if (['notes', 'clientnotes', 'relations', 'charts'].includes(actionType)) {
    return { date: defaultDate };
  }

  return {};
};

const formatCurrency = (value: number) => `Rs ${Number(value || 0).toLocaleString('en-IN')}`;

const getClientOptionLabel = (option: CmsClient) => [
  option.fullName,
  option.phoneNumber || option.whatsappNumber || option.email,
  option.mainTopic,
].filter(Boolean).join(' - ');

const getChartOptionLabel = (option: CmsChartOption) => [
  option.birthDetails?.name || 'Untitled Chart',
  option.birthDetails?.placeOfBirth?.city,
  option.chartType === 'horary' ? 'Horary' : 'Birth Chart',
].filter(Boolean).join(' - ');

const sanitizePdfFileNamePart = (value?: string | null, fallback = 'client') => (
  String(value || fallback).replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || fallback
);

type ProfileDetailState = {
  kind: 'chart' | 'record' | 'relation';
  label: string;
  record: CmsModuleRecord | CmsRelation;
};

type ViewAllState = {
  kind: ProfileDetailState['kind'];
  label: string;
  records: Array<CmsModuleRecord | CmsRelation>;
};

const ClientManagement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { clientId: routeClientId } = useParams<{ clientId?: string }>();
  const { i18n } = useTranslation();
  const { state } = useAuth();
  const isAdminUser = state.user?.role === 'admin' || state.user?.role === 'ADMIN';
  const canAccessCms = true;
  const canSaveCms = Boolean(state.isAuthenticated || isAdminUser);
  const hasPremiumCms = hasPremiumSubscription(state.user);
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const { pdfLanguageSameAsApp, pdfLanguage } = useUserPreferences();
  const pdfDownloadLanguage = resolvePdfLanguage(language, pdfLanguageSameAsApp, pdfLanguage);
  const cmsText = React.useCallback((text?: string | null) => (
    text ? translateInline(language, text, cmsHi[text] || text) : ''
  ), [language]);
  const activeSection = getActiveSection(location.pathname);
  const active = cmsSections.find((item) => item.key === activeSection) || cmsSections[0];
  const [dashboard, setDashboard] = React.useState<DashboardData>(emptyDashboard);
  const [clients, setClients] = React.useState<CmsClient[]>([]);
  const [clientPagination, setClientPagination] = React.useState<PaginationData>({ page: 1, limit: 20, total: 0, pages: 1 });
  const [clientPage, setClientPage] = React.useState(1);
  const [profileClient, setProfileClient] = React.useState<CmsClient | null>(null);
  const [profileLoading, setProfileLoading] = React.useState(false);
  const [moduleRecords, setModuleRecords] = React.useState<CmsModuleRecord[]>([]);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [topicFilter, setTopicFilter] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [dialogAction, setDialogAction] = React.useState<string | null>(null);
  const [editingClient, setEditingClient] = React.useState<CmsClient | null>(null);
  const [form, setForm] = React.useState<Record<string, string>>({});
  const [clientOptions, setClientOptions] = React.useState<CmsClient[]>([]);
  const [clientSearch, setClientSearch] = React.useState('');
  const [clientSearchLoading, setClientSearchLoading] = React.useState(false);
  const [relatedClientOptions, setRelatedClientOptions] = React.useState<CmsClient[]>([]);
  const [relatedClientSearch, setRelatedClientSearch] = React.useState('');
  const [relatedClientSearchLoading, setRelatedClientSearchLoading] = React.useState(false);
  const [chartOptions, setChartOptions] = React.useState<CmsChartOption[]>([]);
  const [selectedCharts, setSelectedCharts] = React.useState<CmsChartOption[]>([]);
  const [chartSearch, setChartSearch] = React.useState('');
  const [chartSearchLoading, setChartSearchLoading] = React.useState(false);
  const [profileDetail, setProfileDetail] = React.useState<ProfileDetailState | null>(null);
  const [viewAllModal, setViewAllModal] = React.useState<ViewAllState | null>(null);
  const [downloadingPdf, setDownloadingPdf] = React.useState('');
  const clientLimitCount = Math.max(dashboard.stats.totalClients || 0, clientPagination.total || 0, clients.length);
  const freeClientLimitReached = canSaveCms && !hasPremiumCms && clientLimitCount >= 2;

  const loadCms = React.useCallback(async () => {
    if (!state.isAuthenticated || !canAccessCms) {
      setDashboard(emptyDashboard);
      setClients([]);
      setModuleRecords([]);
      setError('');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      const shouldLoadRecords = recordModuleKeys.has(activeSection);
      const [dashboardResponse, clientsResponse, recordsResponse] = await Promise.all([
        axios.get('/cms/dashboard'),
        axios.get('/cms/clients', {
          params: {
            search,
            status: statusFilter,
            topic: topicFilter,
            page: clientPage,
            limit: 20,
          },
        }),
        shouldLoadRecords
          ? axios.get(`/cms/records/${activeSection}`, {
            params: {
              search,
              status: statusFilter,
              limit: 50,
            },
          })
          : Promise.resolve(null),
      ]);
      setDashboard({ ...emptyDashboard, ...dashboardResponse.data.data });
      const loadedClients = clientsResponse.data.data.clients || [];
      setClients(loadedClients);
      setClientPagination(clientsResponse.data.data.pagination || { page: clientPage, limit: 20, total: loadedClients.length, pages: 1 });
      setModuleRecords(recordsResponse?.data?.data?.records || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to load Client Management data.');
    } finally {
      setLoading(false);
    }
  }, [state.isAuthenticated, canAccessCms, search, statusFilter, topicFilter, clientPage, activeSection]);

  const loadProfileClient = React.useCallback(async () => {
    if (!state.isAuthenticated || !routeClientId || !canAccessCms) {
      setProfileClient(null);
      setProfileLoading(false);
      setError('');
      return;
    }
    try {
      setProfileLoading(true);
      setError('');
      const response = await axios.get(`/cms/clients/${routeClientId}`);
      setProfileClient(response.data.data.client);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to load client profile.');
    } finally {
      setProfileLoading(false);
    }
  }, [state.isAuthenticated, routeClientId, canAccessCms]);

  React.useEffect(() => {
    const timer = window.setTimeout(loadCms, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [loadCms, search]);

  React.useEffect(() => {
    loadProfileClient();
  }, [loadProfileClient]);

  const openAction = (action: string, initialForm: Record<string, string> = {}) => {
    if (!canSaveCms) {
      setError(cmsText('Create a free account to save this. The layout stays visible so you can see what Premium unlocks.'));
      return;
    }
    if (actionConfig[action]?.type === 'client' && freeClientLimitReached) {
      setError('You can add only 2 clients. Upgrade to Premium for more clients.');
      return;
    }
    const actionType = actionConfig[action]?.type;
    const nextForm = {
      ...getDefaultCmsFormValues(actionType),
      ...initialForm,
    };
    setDialogAction(action);
    setForm(nextForm);
    const initialClient = nextForm.clientId
      ? clients.find((client) => (client._id || client.id) === nextForm.clientId)
      : null;
    setClientSearch(initialClient ? getClientOptionLabel(initialClient) : '');
    setClientOptions(clients.slice(0, 20));
    setRelatedClientSearch(nextForm.relatedClientName || '');
    setRelatedClientOptions([]);
    setChartSearch('');
    setSelectedCharts([]);
    setChartOptions([]);
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogAction(null);
    setForm({});
  };

  const setField = (key: string, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const searchClientsForPicker = React.useCallback(async (query: string) => {
    if (!state.isAuthenticated || !canAccessCms) return;
    try {
      setClientSearchLoading(true);
      const response = await axios.get('/cms/clients', {
        params: {
          search: query,
          limit: 20,
        },
      });
      setClientOptions(response.data.data.clients || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to search clients.');
    } finally {
      setClientSearchLoading(false);
    }
  }, [state.isAuthenticated, canAccessCms]);

  const searchRelatedClientsForPicker = React.useCallback(async (query: string) => {
    if (!state.isAuthenticated || !canAccessCms) return;
    try {
      setRelatedClientSearchLoading(true);
      const response = await axios.get('/cms/clients', {
        params: {
          search: query,
          limit: 20,
        },
      });
      setRelatedClientOptions(response.data.data.clients || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to search related clients.');
    } finally {
      setRelatedClientSearchLoading(false);
    }
  }, [state.isAuthenticated, canAccessCms]);

  const searchChartsForPicker = React.useCallback(async (query: string) => {
    if (!state.isAuthenticated || !canAccessCms) return;
    try {
      setChartSearchLoading(true);
      const response = await axios.get('/charts/my-charts', {
        params: {
          page: 1,
          limit: 20,
          type: 'individual',
          name: query,
        },
      });
      setChartOptions(response.data.data.charts || []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to search charts.');
    } finally {
      setChartSearchLoading(false);
    }
  }, [state.isAuthenticated, canAccessCms]);

  React.useEffect(() => {
    if (!dialogAction || !actionConfig[dialogAction]?.needsClient) return;
    if (!clientSearch.trim()) return;
    const timer = window.setTimeout(() => {
      searchClientsForPicker(clientSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [clientSearch, dialogAction, searchClientsForPicker]);

  React.useEffect(() => {
    if (!dialogAction || actionConfig[dialogAction]?.type !== 'relations') return;
    if (!relatedClientSearch.trim()) {
      setRelatedClientOptions([]);
      return;
    }
    const timer = window.setTimeout(() => {
      searchRelatedClientsForPicker(relatedClientSearch);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dialogAction, relatedClientSearch, searchRelatedClientsForPicker]);

  React.useEffect(() => {
    if (!dialogAction || actionConfig[dialogAction]?.type !== 'charts') return;
    const timer = window.setTimeout(() => {
      searchChartsForPicker(chartSearch);
    }, chartSearch.trim() ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [chartSearch, dialogAction, searchChartsForPicker]);

  const submitDialog = async () => {
    if (!dialogAction) return;
    if (!canSaveCms) {
      setError(cmsText('Create a free account to save this. Pro users can save unlimited clients.'));
      return;
    }
    const config = actionConfig[dialogAction];
    if (!config) return;
    if (config.type === 'client' && !editingClient && freeClientLimitReached) {
      setError('You can add only 2 clients. Upgrade to Premium for more clients.');
      setDialogAction(null);
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (config.type === 'client') {
        await axios.post('/cms/clients', {
          fullName: form.fullName,
          phoneNumber: form.phoneNumber,
          whatsappNumber: form.whatsappNumber,
          email: form.email,
          birthPlace: form.birthPlace,
          currentCity: form.currentCity,
          mainTopic: form.mainTopic,
          clientSource: form.clientSource,
          clientStatus: form.clientStatus || 'New',
          tags: form.tags,
          internalNote: form.internalNote,
        });
      } else if (config.type === 'charts') {
        if (!form.clientId) throw new Error('Please select a client.');
        const chartIds = selectedCharts.map((chart) => chart._id).filter(Boolean);
        if (!chartIds.length) throw new Error('Please select at least one chart.');
        await axios.post(`/cms/clients/${form.clientId}/charts`, {
          chartId: chartIds[0],
          chartIds,
          purpose: form.topic || 'Client Chart',
          notes: form.note,
          tags: form.tags,
        });
      } else {
        if (!form.clientId) throw new Error('Please select a client.');
        if (config.type === 'relations' && !form.relatedClientId) throw new Error('Please select a related client.');
        const payload = {
          title: form.title,
          topic: form.topic,
          note: form.note,
          date: form.date,
          status: form.status,
          amount: form.amount,
          amountCharged: form.amount,
          paymentStatus: form.paymentStatus,
          mode: form.mode,
          consultationTime: form.consultationTime || form.time,
          time: form.consultationTime || form.time,
          linkedChartId: form.linkedChartId,
          summary: form.note,
          relatedClientId: form.relatedClientId,
          relationType: form.relationType,
        };
        if (form.recordId) {
          await axios.put(`/cms/clients/${form.clientId}/${config.type}/${form.recordId}`, payload);
        } else {
          await axios.post(`/cms/clients/${form.clientId}/${config.type}`, payload);
        }
      }

      closeDialog();
      await loadCms();
      await loadProfileClient();
      if (!routeClientId && config.type !== 'client') navigate(['charts', 'relations'].includes(config.type) ? '/cms/clients' : `/cms/${config.type}`);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Unable to save CMS data.');
    } finally {
      setSaving(false);
    }
  };

  const openEditRecord = (record: CmsModuleRecord) => {
    const recordType = record.type || activeSection;
    const actionName = recordActionByType[recordType];
    const recordId = record.id || record._id;
    if (!actionName || !record.clientId || !recordId) {
      setError(cmsText('Unable to edit this CMS record.'));
      return;
    }

    openAction(actionName, {
      recordId,
      clientId: record.clientId,
      title: record.title || '',
      topic: record.topic || '',
      date: toDateInputValue(record.recordDate || record.consultationDate || record.dueDate || record.createdAt),
      consultationTime: record.consultationTime || record.dueTime || record.recordTime || '',
      status: record.status || '',
      amount: String(record.amount || record.amountCharged || ''),
      paymentStatus: record.paymentStatus || '',
      mode: record.mode || '',
      linkedChartId: record.linkedChartId || '',
      note: record.note || record.summary || record.body || '',
    });
  };

  const deleteRecord = async (record: CmsModuleRecord) => {
    const recordType = record.type || activeSection;
    const recordId = record.id || record._id;
    if (!record.clientId || !recordId) {
      setError(cmsText('Unable to delete CMS record.'));
      return;
    }

    if (!window.confirm(cmsText('Delete this record?'))) return;

    try {
      setSaving(true);
      setError('');
      await axios.delete(`/cms/clients/${record.clientId}/${recordType}/${recordId}`);
      await loadCms();
      await loadProfileClient();
    } catch (err: any) {
      setError(err.response?.data?.message || cmsText('Unable to delete CMS record.'));
    } finally {
      setSaving(false);
    }
  };

  const openEditRelation = (relation: CmsRelation) => {
    if (!profileClient) return;
    openAction('Add Relation', {
      recordId: String(relation._id || relation.id || ''),
      clientId: String(profileClient._id || profileClient.id || ''),
      relatedClientId: String(relation.relatedClientId || ''),
      relatedClientName: relation.relatedClientName || '',
      relationType: relation.relationType || '',
      status: relation.status || 'Active',
      linkedChartId: relation.linkedChartId || '',
      note: relation.note || '',
    });
  };

  const deleteRelation = (relation: CmsRelation) => {
    if (!profileClient) return;
    deleteRecord({
      ...relation,
      id: String(relation._id || relation.id || ''),
      type: 'relations',
      clientId: String(profileClient._id || profileClient.id || ''),
    });
  };

  const getRecordTitle = (record: CmsModuleRecord | CmsRelation, fallback: string) => {
    if ('relatedClientName' in record && record.relatedClientName) return record.relatedClientName;
    if ('chartName' in record && record.chartName) return record.chartName;
    if ('title' in record && record.title) return record.title;
    if ('topic' in record && record.topic) return record.topic;
    if ('status' in record && record.status) return record.status;
    return cmsText(fallback);
  };

  const getRecordMeta = (record: CmsModuleRecord | CmsRelation) => [
    'relationType' in record && record.relationType ? cmsText(record.relationType) : '',
    'topic' in record && record.topic ? cmsText(record.topic) : '',
    'status' in record && record.status ? cmsText(record.status) : '',
    'paymentStatus' in record && record.paymentStatus ? cmsText(record.paymentStatus) : '',
    'amount' in record && (record.amount || record.amountCharged) ? formatCurrency(Number(record.amount || record.amountCharged || 0)) : '',
    'recordDate' in record ? formatDate(record.recordDate || record.consultationDate || record.dueDate || record.createdAt) : formatDate(record.createdAt),
    'consultationTime' in record && (record.consultationTime || record.dueTime || record.recordTime) ? record.consultationTime || record.dueTime || record.recordTime : '',
  ].filter((item) => item && item !== '-').join(' · ');

  const getRecordNote = (record: CmsModuleRecord | CmsRelation) => {
    if ('notes' in record && record.notes) return record.notes;
    if ('note' in record && record.note) return record.note;
    if ('summary' in record && record.summary) return record.summary;
    if ('body' in record && record.body) return record.body;
    return '';
  };

  const getDetailRows = (detail: ProfileDetailState) => {
    const record = detail.record;
    const topic = 'topic' in record ? record.topic : '';
    const purpose = 'purpose' in record ? record.purpose : '';
    const tags = 'tags' in record && Array.isArray(record.tags) ? record.tags.join(', ') : '';
    return [
      ['Title', 'title' in record ? record.title || '' : ''],
      ['Topic', topic || purpose || tags],
      ['Status', 'status' in record ? record.status || '' : ''],
      ['Payment Status', 'paymentStatus' in record ? record.paymentStatus || '' : ''],
      ['Relation Type', 'relationType' in record ? record.relationType || '' : ''],
      ['Linked Chart', 'linkedChartId' in record && record.linkedChartId ? 'Yes' : ''],
      ['Date', 'recordDate' in record ? formatDate(record.recordDate || record.consultationDate || record.dueDate || record.createdAt) : formatDate(record.createdAt)],
      ['Time', 'consultationTime' in record ? record.consultationTime || record.dueTime || record.recordTime || '' : ''],
      ['Amount', 'amount' in record && (record.amount || record.amountCharged) ? formatCurrency(Number(record.amount || record.amountCharged || 0)) : ''],
    ].filter(([, value]) => value && value !== '-');
  };

  const archiveClient = async (clientId: string) => {
    if (!canAccessCms) return;
    try {
      await axios.post(`/cms/clients/${clientId}/archive`);
      await loadCms();
      if (routeClientId === clientId) navigate('/cms/clients');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to archive client.');
    }
  };

  const openEditClient = (client: CmsClient) => {
    setEditingClient(client);
    setForm({
      fullName: client.fullName || '',
      phoneNumber: client.phoneNumber || '',
      whatsappNumber: client.whatsappNumber || '',
      email: client.email || '',
      birthPlace: client.birthPlace || '',
      currentCity: client.currentCity || '',
      mainTopic: client.mainTopic || '',
      clientStatus: client.clientStatus || 'New',
      clientSource: client.clientSource || '',
      tags: (client.tags || []).join(', '),
      internalNote: client.internalNote || '',
    });
  };

  const closeEditClient = () => {
    if (saving) return;
    setEditingClient(null);
    setForm({});
  };

  const saveEditedClient = async () => {
    if (!editingClient || !canAccessCms) return;
    try {
      setSaving(true);
      await axios.put(`/cms/clients/${editingClient._id || editingClient.id}`, {
        fullName: form.fullName,
        phoneNumber: form.phoneNumber,
        whatsappNumber: form.whatsappNumber,
        email: form.email,
        birthPlace: form.birthPlace,
        currentCity: form.currentCity,
        mainTopic: form.mainTopic,
        clientSource: form.clientSource,
        clientStatus: form.clientStatus || 'New',
        tags: form.tags,
        internalNote: form.internalNote,
      });
      closeEditClient();
      await loadCms();
      await loadProfileClient();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to update client.');
    } finally {
      setSaving(false);
    }
  };

  const getPdfErrorMessage = async (error: any) => {
    const data = error?.response?.data;
    if (data instanceof Blob) {
      const text = await data.text().catch(() => '');
      try {
        const payload = text ? JSON.parse(text) : {};
        return payload?.message || payload?.error || text || 'Unable to download PDF.';
      } catch {
        return text || 'Unable to download PDF.';
      }
    }
    return data?.message || data?.error || error?.message || 'Unable to download PDF.';
  };

  const downloadCmsPdf = async (url: string, fileName: string, downloadKey: string) => {
    if (!canAccessCms) {
      navigate('/upgrade');
      return;
    }
    try {
      setDownloadingPdf(downloadKey);
      const response = await axios.get(url, { responseType: 'blob', params: { language } });
      const contentType = String(response.headers?.['content-type'] || response.data?.type || '');
      if (!contentType.includes('application/pdf')) {
        const text = await response.data.text().catch(() => '');
        try {
          const payload = text ? JSON.parse(text) : {};
          throw new Error(payload?.message || payload?.error || text || 'Unable to download PDF.');
        } catch (parseError: any) {
          throw new Error(parseError.message || text || 'Unable to download PDF.');
        }
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err: any) {
      setError(await getPdfErrorMessage(err));
    } finally {
      setDownloadingPdf('');
    }
  };

  const stats = [
    ['Total Clients', dashboard.stats.totalClients],
    ['New Clients This Month', dashboard.stats.newClientsThisMonth],
    ['Consultations This Month', dashboard.stats.consultationsThisMonth],
    ['Pending Follow-ups', dashboard.stats.pendingFollowUps],
    ['Active Remedies', dashboard.stats.activeRemedies],
    ['Open Predictions', dashboard.stats.openPredictions],
    ['Pending Payments', formatCurrency(dashboard.stats.pendingPaymentsAmount)],
  ];

  const action = dialogAction ? actionConfig[dialogAction] : null;
  const selectedClient = [...clientOptions, ...clients].find((client) => (client._id || client.id) === form.clientId) || null;
  const selectedRelatedClient = [...relatedClientOptions, ...clients].find((client) => (client._id || client.id) === form.relatedClientId)
    || (form.relatedClientId && form.relatedClientName ? {
      _id: form.relatedClientId,
      fullName: form.relatedClientName,
    } as CmsClient : null);
  const recordRequiresChart = Boolean(action && ['consultations', 'notes', 'clientnotes', 'followups', 'remedies', 'predictions', 'relations', 'payments'].includes(action.type));
  const linkedChartOptions = ((routeClientId ? profileClient?.charts : selectedClient?.charts) || [])
    .filter((chart) => !chart.archivedAt && chart.chartId);
  const visibleClients = activeSection === 'clients' ? clients : dashboard.recentClients;
  const showClientRecords = activeSection === 'dashboard' || activeSection === 'clients';
  const showModuleRecords = recordModuleKeys.has(activeSection);
  const moduleAction = moduleActions[activeSection];
  const primaryAction = moduleAction || 'Add Client';
  const primaryActionLabel = primaryAction;
  const primaryActionForm: Record<string, string> = {};
  const moduleActionLabel = moduleAction;
  const activeRecordStatuses = action?.type === 'consultations'
    ? consultationStatuses
    : action?.type === 'followups'
      ? followUpStatuses
      : action?.type === 'payments'
        ? paymentStatuses
        : recordStatuses;
  const profileRecords = profileClient ? clientRecordGroups.map((group) => ({
    ...group,
    records: ((profileClient as any)[group.recordsKey] || [])
      .filter((record: CmsModuleRecord) => !((record as any).archivedAt))
      .map((record: CmsModuleRecord) => ({
        ...record,
        id: String(record._id || record.id || ''),
        type: group.key,
        typeLabel: group.typeLabel,
        title: record.title || record.topic || record.status || group.typeLabel,
        clientId: profileClient._id || profileClient.id,
        clientName: profileClient.fullName,
        clientPhone: profileClient.phoneNumber || profileClient.whatsappNumber || profileClient.email || '',
        recordDate: record.recordDate || record.consultationDate || record.dueDate || record.createdAt,
      })),
  })) : [];
  const visibleProfileCharts = (profileClient?.charts || []).filter((chart) => !chart.archivedAt);
  const visibleProfileRelations = (profileClient?.relations || []).filter((relation) => relation.status !== 'Archived');
  const renderLinkedChartField = () => recordRequiresChart ? (
    <TextField select label={cmsText('Linked Chart')} value={form.linkedChartId || ''} onChange={(event) => setField('linkedChartId', event.target.value)} required helperText={!linkedChartOptions.length ? cmsText('Link a chart to this client before adding records.') : cmsText('Select linked chart')}>
      <MenuItem value="">{cmsText('Select linked chart')}</MenuItem>
      {linkedChartOptions.map((chart) => (
        <MenuItem key={chart._id || chart.id || chart.chartId} value={chart.chartId || ''}>
          {[chart.chartName || cmsText('Client Chart'), chart.purpose, chart.chartType].filter(Boolean).join(' - ')}
        </MenuItem>
      ))}
    </TextField>
  ) : null;
  const renderRecordDialogFields = () => {
    if (action?.type === 'consultations') {
      return (
        <>
          {renderLinkedChartField()}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={cmsText('Topic')} value={form.topic || ''} onChange={(event) => setField('topic', event.target.value)} fullWidth autoFocus />
            <TextField label={cmsText('Date')} type="date" value={form.date || ''} onChange={(event) => setField('date', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label={cmsText('Time')} type="time" value={form.consultationTime || ''} onChange={(event) => setField('consultationTime', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label={cmsText('Mode')} value={form.mode || ''} onChange={(event) => setField('mode', event.target.value)} fullWidth>
              <MenuItem value="">{cmsText('No Mode')}</MenuItem>
              {consultationModes.map((mode) => <MenuItem key={mode} value={mode}>{cmsText(mode)}</MenuItem>)}
            </TextField>
            <TextField select label={cmsText('Status')} value={form.status || ''} onChange={(event) => setField('status', event.target.value)} fullWidth>
              <MenuItem value="">{cmsText('No Status')}</MenuItem>
              {activeRecordStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label={cmsText('Payment Status')} value={form.paymentStatus || ''} onChange={(event) => setField('paymentStatus', event.target.value)} fullWidth>
              <MenuItem value="">{cmsText('No Payment Status')}</MenuItem>
              {paymentStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
            </TextField>
            <TextField label={cmsText('Amount')} type="number" value={form.amount || ''} onChange={(event) => setField('amount', event.target.value)} fullWidth />
          </Stack>
          <TextField label={cmsText('Summary')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
        </>
      );
    }

    if (action?.type === 'notes' || action?.type === 'clientnotes') {
      return (
        <>
          {renderLinkedChartField()}
          <TextField label={cmsText('Title')} value={form.title || ''} onChange={(event) => setField('title', event.target.value)} autoFocus />
          <TextField label={cmsText('Notes')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
        </>
      );
    }

    if (action?.type === 'followups') {
      return (
        <>
          {renderLinkedChartField()}
          <TextField label={cmsText('Topic')} value={form.topic || ''} onChange={(event) => setField('topic', event.target.value)} autoFocus />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={cmsText('Date')} type="date" value={form.date || ''} onChange={(event) => setField('date', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label={cmsText('Time')} type="time" value={form.consultationTime || ''} onChange={(event) => setField('consultationTime', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField label={cmsText('Details')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
        </>
      );
    }

    if (action?.type === 'remedies' || action?.type === 'predictions') {
      return (
        <>
          {renderLinkedChartField()}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={cmsText('Date')} type="date" value={form.date || ''} onChange={(event) => setField('date', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth autoFocus />
            <TextField label={cmsText('Time')} type="time" value={form.consultationTime || ''} onChange={(event) => setField('consultationTime', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
          <TextField label={cmsText('Details')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
        </>
      );
    }

    return (
      <>
        {renderLinkedChartField()}
        <TextField label={cmsText('Title')} value={form.title || ''} onChange={(event) => setField('title', event.target.value)} autoFocus />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label={cmsText('Topic')} value={form.topic || ''} onChange={(event) => setField('topic', event.target.value)} fullWidth />
          <TextField label={cmsText('Date')} type="date" value={form.date || ''} onChange={(event) => setField('date', event.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField select label={cmsText('Status')} value={form.status || ''} onChange={(event) => setField('status', event.target.value)} fullWidth>
            <MenuItem value="">{cmsText('No Status')}</MenuItem>
            {activeRecordStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
          </TextField>
          <TextField label={cmsText('Amount')} type="number" value={form.amount || ''} onChange={(event) => setField('amount', event.target.value)} fullWidth />
        </Stack>
        <TextField label={cmsText('Note / Summary')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
      </>
    );
  };
  const renderProfileDialogs = () => (
    <>
      <Dialog open={Boolean(dialogAction)} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText(action?.title || 'CMS Action')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {action?.needsClient && (
              <TextField label={cmsText('Client')} value={profileClient?.fullName || ''} InputProps={{ readOnly: true }} />
            )}
            {action?.type === 'charts' ? (
              <>
                <Autocomplete
                  multiple
                  options={chartOptions}
                  value={selectedCharts}
                  inputValue={chartSearch}
                  loading={chartSearchLoading}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input') setChartSearch(value);
                    if (reason === 'clear') setChartSearch('');
                  }}
                  onChange={(_event, value) => {
                    setSelectedCharts(value);
                    setChartSearch('');
                  }}
                  getOptionLabel={getChartOptionLabel}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  filterOptions={(options) => options}
                  noOptionsText={chartSearch ? cmsText('No charts found') : cmsText('Type to search saved charts')}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={cmsText('Charts')}
                      required
                      helperText={cmsText('Search your saved charts by chart name')}
                    />
                  )}
                />
                <TextField label={cmsText('Purpose')} value={form.topic || ''} onChange={(event) => setField('topic', event.target.value)} placeholder={cmsText('Client Chart')} />
                <TextField label={cmsText('Tags')} helperText={cmsText('Comma separated')} value={form.tags || ''} onChange={(event) => setField('tags', event.target.value)} />
                <TextField label={cmsText('Notes')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={3} />
              </>
            ) : action?.type === 'relations' ? (
              <>
                <Autocomplete
                  options={relatedClientOptions}
                  value={selectedRelatedClient}
                  inputValue={relatedClientSearch}
                  loading={relatedClientSearchLoading}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input') setRelatedClientSearch(value);
                    if (reason === 'clear') setRelatedClientSearch('');
                  }}
                  onChange={(_event, value) => {
                    setField('relatedClientId', value?._id || value?.id || '');
                    setField('relatedClientName', value?.fullName || '');
                    setRelatedClientSearch(value ? getClientOptionLabel(value) : '');
                  }}
                  getOptionLabel={getClientOptionLabel}
                  isOptionEqualToValue={(option, value) => (option._id || option.id) === (value._id || value.id)}
                  filterOptions={(options) => options.filter((option) => (option._id || option.id) !== form.clientId)}
                  noOptionsText={relatedClientSearch ? cmsText('No clients found') : cmsText('Type to search related client')}
                  renderOption={(props, option, { selected }) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        color: 'text.primary',
                        bgcolor: selected ? 'rgba(46, 125, 82, 0.10)' : 'background.paper',
                        '&.Mui-focused': {
                          bgcolor: 'rgba(46, 125, 82, 0.08)',
                        },
                        '&[aria-selected="true"]': {
                          bgcolor: 'rgba(46, 125, 82, 0.12)',
                          color: 'text.primary',
                        },
                      }}
                    >
                      {getClientOptionLabel(option)}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={cmsText('Related Client')}
                      required
                      helperText={cmsText('Connect this client with another saved client profile')}
                    />
                  )}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label={cmsText('Relation Type')} value={form.relationType || ''} onChange={(event) => setField('relationType', event.target.value)} required fullWidth>
                    {relationTypes.map((relationType) => <MenuItem key={relationType} value={relationType}>{cmsText(relationType)}</MenuItem>)}
                  </TextField>
                  <TextField select label={cmsText('Status')} value={form.status || 'Active'} onChange={(event) => setField('status', event.target.value)} fullWidth>
                    {relationStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField select label={cmsText('Linked Chart')} value={form.linkedChartId || ''} onChange={(event) => setField('linkedChartId', event.target.value)} required helperText={!linkedChartOptions.length ? cmsText('Link a chart to this client before adding records.') : cmsText('Select linked chart')}>
                  <MenuItem value="">{cmsText('Select linked chart')}</MenuItem>
                  {linkedChartOptions.map((chart) => (
                    <MenuItem key={chart._id || chart.id || chart.chartId} value={chart.chartId || ''}>
                      {[chart.chartName || cmsText('Client Chart'), chart.purpose, chart.chartType].filter(Boolean).join(' - ')}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField label={cmsText('Notes')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={4} />
              </>
            ) : (
              renderRecordDialogFields()
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>{cmsText('Cancel')}</Button>
          <Button onClick={submitDialog} variant="contained" disabled={saving || (action?.needsClient && !form.clientId) || (recordRequiresChart && !form.linkedChartId) || (action?.type === 'charts' && !selectedCharts.length) || (action?.type === 'relations' && (!form.relatedClientId || !form.relationType))}>
            {saving ? cmsText('Saving') : cmsText('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingClient)} onClose={closeEditClient} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText('Edit Client')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label={cmsText('Full Name')} value={form.fullName || ''} onChange={(event) => setField('fullName', event.target.value)} required autoFocus />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={cmsText('Phone Number')} value={form.phoneNumber || ''} onChange={(event) => setField('phoneNumber', event.target.value)} fullWidth />
              <TextField label={cmsText('WhatsApp Number')} value={form.whatsappNumber || ''} onChange={(event) => setField('whatsappNumber', event.target.value)} fullWidth />
            </Stack>
            <TextField label={cmsText('Email')} value={form.email || ''} onChange={(event) => setField('email', event.target.value)} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={cmsText('Birth Place')} value={form.birthPlace || ''} onChange={(event) => setField('birthPlace', event.target.value)} fullWidth />
              <TextField label={cmsText('Current City')} value={form.currentCity || ''} onChange={(event) => setField('currentCity', event.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label={cmsText('Main Topic')} value={form.mainTopic || ''} onChange={(event) => setField('mainTopic', event.target.value)} fullWidth>
                <MenuItem value="">{cmsText('No Topic')}</MenuItem>
                {clientTopics.map((topic) => <MenuItem key={topic} value={topic}>{cmsText(topic)}</MenuItem>)}
              </TextField>
              <TextField select label={cmsText('Status')} value={form.clientStatus || 'New'} onChange={(event) => setField('clientStatus', event.target.value)} fullWidth>
                {clientStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label={cmsText('Internal Note')} value={form.internalNote || ''} onChange={(event) => setField('internalNote', event.target.value)} multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditClient} disabled={saving}>{cmsText('Cancel')}</Button>
          <Button onClick={saveEditedClient} variant="contained" disabled={saving || !form.fullName}>
            {saving ? cmsText('Saving') : cmsText('Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(profileDetail)} onClose={() => setProfileDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText(profileDetail?.label || 'Record')}</DialogTitle>
        <DialogContent dividers>
          {profileDetail && (
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 850 }}>
                {getRecordTitle(profileDetail.record, profileDetail.label)}
              </Typography>
              {getDetailRows(profileDetail).map(([label, value]) => (
                <Box key={`${label}-${value}`} sx={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{cmsText(String(label))}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>{cmsText(String(value))}</Typography>
                </Box>
              ))}
              {getRecordNote(profileDetail.record) && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {getRecordNote(profileDetail.record)}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {profileDetail?.kind === 'record' && (
            <Button
              variant="outlined"
              onClick={() => {
                const record = profileDetail.record as CmsModuleRecord;
                setProfileDetail(null);
                openEditRecord(record);
              }}
            >
              {cmsText('Edit')}
            </Button>
          )}
          {profileDetail?.kind === 'relation' && (
            <Button
              variant="outlined"
              onClick={() => {
                const relation = profileDetail.record as CmsRelation;
                setProfileDetail(null);
                openEditRelation(relation);
              }}
            >
              {cmsText('Edit')}
            </Button>
          )}
          <Button onClick={() => setProfileDetail(null)} variant="contained">{cmsText('Close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(viewAllModal)} onClose={() => setViewAllModal(null)} fullWidth maxWidth="md">
        <DialogTitle>{cmsText(viewAllModal?.label || 'Records')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {viewAllModal?.records.length ? viewAllModal.records.map((record, index) => (
              <Paper key={`${viewAllModal.kind}-${(record as any)._id || (record as any).id || index}`} elevation={0} sx={{ p: 1.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1.25}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 850 }}>
                        {index + 1}. {getRecordTitle(record, viewAllModal.label)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getRecordMeta(record) || '-'}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setProfileDetail({ kind: viewAllModal.kind, label: viewAllModal.label, record });
                          setViewAllModal(null);
                        }}
                      >
                        {cmsText('View')}
                      </Button>
                      {viewAllModal.kind === 'record' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setViewAllModal(null);
                            openEditRecord(record as CmsModuleRecord);
                          }}
                        >
                          {cmsText('Edit')}
                        </Button>
                      )}
                      {viewAllModal.kind === 'relation' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setViewAllModal(null);
                            openEditRelation(record as CmsRelation);
                          }}
                        >
                          {cmsText('Edit')}
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                  {getDetailRows({ kind: viewAllModal.kind, label: viewAllModal.label, record }).map(([label, value]) => (
                    <Box key={`${label}-${value}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '110px minmax(0, 1fr)', sm: '150px minmax(0, 1fr)' }, gap: 1.5 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{cmsText(String(label))}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 750, overflowWrap: 'anywhere' }}>{cmsText(String(value))}</Typography>
                    </Box>
                  ))}
                  {getRecordNote(record) && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {getRecordNote(record)}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            )) : (
              <Alert severity="info">{cmsText('No records found for the current filters.')}</Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewAllModal(null)}>{cmsText('Close')}</Button>
          {profileClient && (
            <Button
              variant="contained"
              startIcon={downloadingPdf === 'profile-view-all' ? <CircularProgress size={16} /> : <Download />}
              onClick={() => downloadCmsPdf(`/cms/clients/${profileClient._id || profileClient.id}/export/pdf`, `${sanitizePdfFileNamePart(profileClient.fullName)}_client_management.pdf`, 'profile-view-all')}
              disabled={Boolean(downloadingPdf)}
            >
              {cmsText('Download PDF')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  const renderCmsSidebarItem = (item: typeof cmsSections[number], selectedKey: CmsSectionKey = activeSection) => {
    const itemContent = (
      <>
        <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
        <ListItemText primary={cmsText(item.label)} primaryTypographyProps={{ fontWeight: selectedKey === item.key ? 800 : 600 }} />
        {!canAccessCms && <LockIcon sx={{ fontSize: 17, color: 'text.disabled' }} />}
      </>
    );

    if (!canAccessCms) {
      return (
        <ListItemButton
          key={item.key}
          disabled
          sx={{
            py: 1.05,
            opacity: 0.72,
            '&.Mui-disabled': {
              color: 'text.primary',
              WebkitTextFillColor: 'inherit',
            },
            '&.Mui-disabled .MuiListItemIcon-root': {
              color: 'text.secondary',
            },
          }}
        >
          {itemContent}
        </ListItemButton>
      );
    }

    return (
      <ListItemButton key={item.key} component={RouterLink} to={item.path} selected={selectedKey === item.key} sx={{ py: 1.05 }}>
        {itemContent}
      </ListItemButton>
    );
  };

  const renderLockedCmsAccess = () => (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 3, md: 4 },
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        minHeight: 460,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      }}
    >
      <Stack spacing={2.25} alignItems="center" sx={{ maxWidth: 760 }}>
        <Box
          sx={{
            width: 68,
            height: 68,
            borderRadius: '50%',
            bgcolor: 'rgba(46, 125, 82, 0.10)',
            color: 'primary.main',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <LockIcon sx={{ fontSize: 34 }} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 850, color: 'primary.main', mb: 1 }}>
            {cmsText('Premium Pro unlocks Client Management')}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: '1.04rem' }}>
            {cmsText('Upgrade to manage client profiles, consultations, notes, follow-ups, remedies, predictions, analytics and payment tracking in one workspace.')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {cmsSections.filter((item) => item.key !== 'dashboard').map((item) => (
            <Chip key={item.key} variant="outlined" icon={<LockIcon />} label={cmsText(item.label)} />
          ))}
        </Box>
        <Button variant="contained" size="large" onClick={() => navigate('/upgrade')} startIcon={<LockIcon />}>
          {cmsText('Upgrade to Premium Pro')}
        </Button>
      </Stack>
    </Paper>
  );

  if (false && !state.isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => navigate('/login')}>{cmsText('Login')}</Button>}>
          {cmsText('Please login to use Client Management. CMS data is stored per account.')}
        </Alert>
      </Container>
    );
  }

  if (routeClientId) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, md: 3 } }}>
        <Container maxWidth="xl">
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
            <Paper elevation={0} sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText('Client Management')}</Typography>
                <Typography variant="body2" color="text.secondary">{cmsText('Client Profile')}</Typography>
              </Box>
              <Divider />
              <List dense disablePadding>
                {cmsSections.map((item) => renderCmsSidebarItem(item, 'clients'))}
              </List>
            </Paper>

            <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
              {!canAccessCms ? renderLockedCmsAccess() : (
                <>
              <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
                  <Box>
                    <Button size="small" variant="outlined" onClick={() => navigate('/cms/clients')} sx={{ mb: 1 }}>
                      {cmsText('Back to Clients')}
                    </Button>
                    <Typography variant="h4" sx={{ fontWeight: 850, color: 'primary.main' }}>{profileClient?.fullName || cmsText('Client Profile')}</Typography>
                    <Typography color="text.secondary">{profileClient?.phoneNumber || profileClient?.whatsappNumber || profileClient?.email || cmsText('No contact added')}</Typography>
                  </Box>
	                  {profileClient && (
	                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
	                      <Button
	                        variant="outlined"
	                        startIcon={downloadingPdf === 'profile' ? <CircularProgress size={16} /> : <Download />}
	                        onClick={() => downloadCmsPdf(`/cms/clients/${profileClient._id || profileClient.id}/export/pdf`, `${sanitizePdfFileNamePart(profileClient.fullName)}_client_management.pdf`, 'profile')}
	                        disabled={Boolean(downloadingPdf)}
	                      >
	                        {cmsText('Download PDF')}
	                      </Button>
	                      <Button variant="outlined" onClick={() => openEditClient(profileClient)}>{cmsText('Edit')}</Button>
                      <Button variant="outlined" onClick={() => openAction('Add Personal Note', { clientId: profileClient._id || profileClient.id || '' })}>{cmsText('Add Personal Note')}</Button>
                      <Button variant="outlined" onClick={() => openAction('Add Note to Client', { clientId: profileClient._id || profileClient.id || '' })}>{cmsText('Add Note to Client')}</Button>
                      <Button color="warning" onClick={() => archiveClient(profileClient._id || profileClient.id || '')}>{cmsText('Archive')}</Button>
                    </Stack>
                  )}
                </Stack>
              </Paper>

              {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

              {profileLoading ? (
                <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <CircularProgress />
                  <Typography sx={{ mt: 2 }} color="text.secondary">{cmsText('Loading live CMS data...')}</Typography>
                </Paper>
              ) : profileClient ? (
                <>
                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{cmsText('Client Details')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                      {[
                        ['Status', cmsText(profileClient.clientStatus || 'New')],
                        ['Main Topic', profileClient.mainTopic ? cmsText(profileClient.mainTopic) : '-'],
                        ['Source', profileClient.clientSource ? cmsText(profileClient.clientSource) : '-'],
                        ['Email', profileClient.email || '-'],
                        ['Phone Number', profileClient.phoneNumber || '-'],
                        ['WhatsApp Number', profileClient.whatsappNumber || '-'],
                        ['Birth Place', profileClient.birthPlace || '-'],
                        ['Current City', profileClient.currentCity || '-'],
                        ['Date of Birth', formatDate(profileClient.dateOfBirth)],
                        ['Time of Birth', profileClient.timeOfBirth || '-'],
                        ['Last Consultation', formatDate(profileClient.lastConsultationAt)],
                        ['Next Follow-up', formatDate(profileClient.nextFollowUpAt)],
                      ].map(([label, value]) => (
                        <Box key={label} sx={{ p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" color="text.secondary">{cmsText(String(label))}</Typography>
                          <Typography sx={{ fontWeight: 800 }}>{value}</Typography>
                        </Box>
                      ))}
                    </Box>
                    {profileClient.internalNote && (
                      <Typography sx={{ mt: 2 }} color="text.secondary">{profileClient.internalNote}</Typography>
                    )}
                  </Paper>

                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                    {[
                      ['Charts', profileClient.counts?.charts || 0],
                      ['Personal Notes', profileClient.counts?.notes || 0],
                      ['Notes to Clients', profileClient.counts?.clientNotes || 0],
                      ['Remedies', profileClient.counts?.remedies || 0],
                      ['Predictions', profileClient.counts?.predictions || 0],
                      ['Relations', profileClient.counts?.relations || 0],
                      ['Payments', profileClient.counts?.payments || 0],
                      ['Follow-ups', profileClient.counts?.followUps || 0],
                    ].map(([label, value]) => (
                      <Paper key={label} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="h5" sx={{ fontWeight: 850 }}>{value}</Typography>
                        <Typography variant="body2" color="text.secondary">{cmsText(String(label))}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText('Charts')}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {visibleProfileCharts.length > 0 && (
                          <Button size="small" variant="outlined" onClick={() => setViewAllModal({ kind: 'chart', label: 'Charts', records: visibleProfileCharts })}>{cmsText('View All')}</Button>
                        )}
                        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openAction('Assign Chart', { clientId: profileClient._id || profileClient.id || '' })}>{cmsText('Assign Chart')}</Button>
                      </Stack>
                    </Stack>
                    {visibleProfileCharts.length ? (
                      <Stack spacing={1.25}>
                        {visibleProfileCharts.map((chart) => (
                          <Box key={chart._id || chart.id || chart.chartId} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', md: 'minmax(220px, 1fr) auto' }, gap: { xs: 1, md: 2 }, p: 1.75, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: 'minmax(120px, max-content) minmax(0, 1fr)', gap: 1.25, alignItems: 'center', cursor: 'pointer' }} onClick={() => setProfileDetail({ kind: 'chart', label: 'Charts', record: chart })}>
                              <Typography sx={{ fontWeight: 850 }} noWrap>{chart.chartName || chart.title || cmsText('Client Chart')}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{getRecordNote(chart) || chart.purpose || '-'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton size="small" color="primary" aria-label={cmsText('View')} disabled={!chart.chartId} onClick={() => chart.chartId && navigate(`/chart/${chart.chartId}`)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="primary" aria-label={cmsText('Download PDF')} disabled={!chart.chartId || Boolean(downloadingPdf)} onClick={() => chart.chartId && downloadCmsPdf(`/charts/${chart.chartId}/pdf?language=${encodeURIComponent(pdfDownloadLanguage)}`, `${sanitizePdfFileNamePart(chart.chartName || profileClient.fullName)}_report.pdf`, `chart-${chart.chartId}`)}>
                                {downloadingPdf === `chart-${chart.chartId}` ? <CircularProgress size={18} /> : <Download fontSize="small" />}
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label="Download Client Report" disabled={!chart.chartId || Boolean(downloadingPdf)} onClick={() => chart.chartId && downloadCmsPdf(`/charts/${chart.chartId}/pdf?language=${encodeURIComponent(pdfDownloadLanguage)}&clientReport=1`, `${sanitizePdfFileNamePart(chart.chartName || profileClient.fullName)}_client_report.pdf`, `client-report-${chart.chartId}`)}>
                                {downloadingPdf === `client-report-${chart.chartId}` ? <CircularProgress size={18} /> : <ContactPage fontSize="small" />}
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label={cmsText('Delete')} onClick={() => deleteRecord({
                                ...chart,
                                id: String(chart._id || chart.id || ''),
                                type: 'charts',
                                clientId: profileClient._id || profileClient.id,
                              })}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Alert severity="info">{cmsText('No charts found')}</Alert>
                    )}
                  </Paper>

                  {profileRecords.map((group) => (
                    <Paper key={group.key} elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText(group.label)}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {group.records.length > 0 && (
                            <Button size="small" variant="outlined" onClick={() => setViewAllModal({ kind: 'record', label: group.label, records: group.records })}>{cmsText('View All')}</Button>
                          )}
                          <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openAction(recordActionByType[group.key], { clientId: profileClient._id || profileClient.id || '' })}>
                            {cmsText(recordActionByType[group.key])}
                          </Button>
                        </Stack>
                      </Stack>
                      <Stack spacing={1.25}>
                        {group.records.length ? group.records.map((record: CmsModuleRecord) => (
                          <Box key={`${group.key}-${record.id || record._id}`} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: 'minmax(0, 1fr) auto' }, gap: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(110px, max-content) minmax(0, 1fr)', sm: 'minmax(160px, max-content) minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center', cursor: 'pointer' }} onClick={() => setProfileDetail({ kind: 'record', label: group.label, record })}>
                              <Typography sx={{ fontWeight: 850 }} noWrap>{getRecordTitle(record, group.typeLabel)}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{getRecordMeta(record) || getRecordNote(record) || '-'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton size="small" color="primary" aria-label={cmsText('View')} onClick={() => setProfileDetail({ kind: 'record', label: group.label, record })}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label={cmsText('Delete')} onClick={() => deleteRecord(record)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        )) : (
                          <Alert severity="info">{cmsText('No records found for the current filters.')}</Alert>
                        )}
                      </Stack>
                    </Paper>
                  ))}

                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText('Relations')}</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {visibleProfileRelations.length > 0 && (
                          <Button size="small" variant="outlined" onClick={() => setViewAllModal({ kind: 'relation', label: 'Relations', records: visibleProfileRelations })}>{cmsText('View All')}</Button>
                        )}
                        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openAction('Add Relation', { clientId: profileClient._id || profileClient.id || '', status: 'Active' })}>{cmsText('Add Relation')}</Button>
                      </Stack>
                    </Stack>
                    {visibleProfileRelations.length ? (
                      <Stack spacing={1}>
                        {visibleProfileRelations.map((relation) => (
                          <Box key={relation._id || relation.id || `${relation.relatedClientId}-${relation.relationType}`} sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 1, p: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', alignItems: 'center' }}>
                            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(110px, max-content) minmax(0, 1fr)', sm: 'minmax(160px, max-content) minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center', cursor: 'pointer' }} onClick={() => setProfileDetail({ kind: 'relation', label: 'Relations', record: relation })}>
                              <Typography sx={{ fontWeight: 850 }} noWrap>{relation.relatedClientName || cmsText('Related Client')}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{getRecordMeta(relation) || getRecordNote(relation) || '-'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton size="small" color="primary" aria-label={cmsText('View')} onClick={() => setProfileDetail({ kind: 'relation', label: 'Relations', record: relation })}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label={cmsText('Delete')} onClick={() => deleteRelation(relation)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">{cmsText('No connected profiles yet.')}</Typography>
                    )}
                  </Paper>
                </>
              ) : (
                <Alert severity="info">{cmsText('Select a client record to view details.')}</Alert>
              )}
                </>
              )}
            </Stack>
          </Stack>
        </Container>
        {renderProfileDialogs()}
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: { xs: 2, md: 3 } }}>
      <Container maxWidth="xl">
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5}>
          <Paper elevation={0} sx={{ width: { xs: '100%', md: 260 }, flexShrink: 0, borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText('Client Management')}</Typography>
              <Typography variant="body2" color="text.secondary">{cmsText('CMS for astrologer client work')}</Typography>
            </Box>
            <Divider />
            <List dense disablePadding>
              {cmsSections.map((item) => renderCmsSidebarItem(item))}
            </List>
          </Paper>

          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            {!canAccessCms ? renderLockedCmsAccess() : (
              <>
            <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Stack direction={{ xs: 'column', xl: 'row' }} spacing={2.25} alignItems={{ xl: 'flex-start' }} justifyContent="space-between">
                <Box sx={{ flex: { xs: '0 1 auto', xl: '1 1 520px' }, minWidth: 0 }}>
                  <Typography variant="h4" sx={{ fontWeight: 850, color: 'primary.main' }}>{cmsText(active.label)}</Typography>
                  <Typography color="text.secondary" sx={{ maxWidth: 980 }}>{cmsText(sectionSummaries[active.key])}</Typography>
                </Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  useFlexGap
                  sx={{
                    width: { xs: '100%', xl: 'auto' },
                    flex: { xl: '0 0 auto' },
                    flexWrap: { sm: 'wrap', xl: 'nowrap' },
                    justifyContent: { xs: 'stretch', sm: 'flex-start', xl: 'flex-end' }
                  }}
                >
                  <TextField
                    size="small"
                    value={search}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setClientPage(1);
                    }}
                    placeholder={cmsText('Search clients, phone, topic')}
                    InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    sx={{
                      width: { xs: '100%', sm: 340, xl: 360 },
                      '& .MuiInputBase-root': { height: 48 }
                    }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => openAction(primaryAction, primaryActionForm)}
                    sx={{ minWidth: { xs: '100%', sm: 150 }, height: 48, px: 2.25, whiteSpace: 'nowrap' }}
                  >
                    {cmsText(primaryActionLabel)}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={downloadingPdf === 'all' ? <CircularProgress size={16} /> : <Download />}
                    onClick={() => downloadCmsPdf('/cms/clients/export/pdf', 'kundlistar_client_management.pdf', 'all')}
                    disabled={Boolean(downloadingPdf)}
                    sx={{ minWidth: { xs: '100%', sm: 170 }, height: 48, px: 2.25, whiteSpace: 'nowrap' }}
                  >
                    {cmsText('Download PDF')}
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

            {freeClientLimitReached && (
              <Alert
                severity="info"
                action={<Button color="inherit" size="small" onClick={() => navigate('/upgrade')}>Upgrade</Button>}
              >
                You can add only 2 clients. Upgrade to Premium for more clients.
              </Alert>
            )}

            {loading ? (
              <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">{cmsText('Loading live CMS data...')}</Typography>
              </Paper>
            ) : (
              <>
                {activeSection === 'dashboard' && (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                      {stats.map(([label, value]) => (
                        <Box key={label}>
                          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                            <Typography variant="h5" sx={{ fontWeight: 850 }}>{value}</Typography>
                            <Typography variant="body2" color="text.secondary">{cmsText(String(label))}</Typography>
                          </Paper>
                        </Box>
                      ))}
                    </Box>

                    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5} sx={{ mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText("Today's Consultations")}</Typography>
                          <Typography variant="body2" color="text.secondary">{cmsText('Consultations scheduled for today from real client records.')}</Typography>
                        </Box>
                        <Button size="small" variant="contained" startIcon={<Add />} onClick={() => openAction('Add Consultation')}>{cmsText('Add Consultation')}</Button>
                      </Stack>
                      <Stack spacing={1.25}>
                        {dashboard.todayConsultations.length ? dashboard.todayConsultations.map((record) => (
                          <Box
                            key={record._id || record.id || `${record.clientId}-${record.consultationDate}`}
                            onClick={() => setProfileDetail({ kind: 'record', label: 'Consultations', record: { ...record, id: String(record._id || record.id || ''), type: 'consultations', clientId: String(record.clientId || '') } })}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'minmax(0, 1fr) auto',
                              gap: 1,
                              p: 1.5,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              alignItems: 'center',
                              cursor: 'pointer',
                              '&:hover': { borderColor: 'primary.light', bgcolor: 'rgba(46, 125, 82, 0.04)' },
                            }}
                          >
                            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(110px, max-content) minmax(0, 1fr)', sm: 'minmax(180px, max-content) minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center' }}>
                              <Typography sx={{ fontWeight: 850 }} noWrap>{record.topic || record.summary || cmsText('Consultation')}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {[record.clientName || cmsText('Client'), record.status, formatDate(record.consultationDate || record.createdAt), record.consultationTime].filter(Boolean).join(' · ')}
                              </Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton size="small" color="primary" aria-label={cmsText('View')} onClick={(event) => { event.stopPropagation(); setProfileDetail({ kind: 'record', label: 'Consultations', record: { ...record, id: String(record._id || record.id || ''), type: 'consultations', clientId: String(record.clientId || '') } }); }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label={cmsText('Delete')} onClick={(event) => { event.stopPropagation(); deleteRecord({ ...record, id: String(record._id || record.id || ''), type: 'consultations', clientId: String(record.clientId || '') }); }}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        )) : (
                          <Alert severity="info">{cmsText('No consultations scheduled for today.')}</Alert>
                        )}
                      </Stack>
                    </Paper>
                  </>
                )}

                <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ md: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText('Quick Actions')}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {dashboard.quickActions.map((item) => (
                        <Button key={item} size="small" variant="outlined" startIcon={<Add />} onClick={() => openAction(item)}>
                          {cmsText(item)}
                        </Button>
                      ))}
                    </Stack>
                  </Stack>
                </Paper>

                {showClientRecords && (
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.5 }}>
                  <Box>
                    <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1.5 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {cmsText('Client Records')}
                        </Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            select
                            size="small"
                            label={cmsText('Status')}
                            value={statusFilter}
                            onChange={(event) => {
                              setStatusFilter(event.target.value);
                              setClientPage(1);
                            }}
                            sx={{ minWidth: 170 }}
                          >
                            <MenuItem value="">{cmsText('All Statuses')}</MenuItem>
                            {clientStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label={cmsText('Topic')}
                            value={topicFilter}
                            onChange={(event) => {
                              setTopicFilter(event.target.value);
                              setClientPage(1);
                            }}
                            sx={{ minWidth: 170 }}
                          >
                            <MenuItem value="">{cmsText('All Topics')}</MenuItem>
                            {clientTopics.map((topic) => <MenuItem key={topic} value={topic}>{cmsText(topic)}</MenuItem>)}
                          </TextField>
                        </Stack>
                      </Stack>
                      <Stack spacing={1.25}>
                        {visibleClients.length ? (
                          visibleClients.map((client) => {
                            const clientId = client._id || client.id || '';
                            return (
                              <Box
                                key={clientId}
                                onClick={() => navigate(`/cms/clients/${clientId}`)}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: 'minmax(190px, 1fr) minmax(240px, 1.2fr) minmax(230px, auto)' },
                                  gap: { xs: 1.25, md: 2 },
                                  alignItems: 'center',
                                  p: 1.75,
                                  borderRadius: 1.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: 'background.paper',
                                  cursor: 'pointer',
                                  transition: 'border-color 0.15s ease, background-color 0.15s ease',
                                  '&:hover': {
                                    borderColor: 'primary.light',
                                    bgcolor: 'rgba(46, 125, 82, 0.04)'
                                  }
                                }}
                              >
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography sx={{ fontWeight: 850, fontSize: '1.02rem' }} noWrap>{client.fullName}</Typography>
                                  <Typography variant="body2" color="text.secondary" noWrap>
                                    {client.phoneNumber || client.whatsappNumber || client.email || cmsText('No contact added')}
                                  </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
                                  <Chip size="small" variant="outlined" label={cmsText(client.mainTopic || 'No topic')} />
                                  <Chip size="small" color={client.clientStatus === 'Follow-up Pending' ? 'warning' : 'default'} label={cmsText(client.clientStatus || 'New')} />
                                  {client.nextFollowUpAt && (
                                    <Chip size="small" color="primary" variant="outlined" label={`${cmsText('Follow-ups')} ${formatDate(client.nextFollowUpAt)}`} />
                                  )}
                                </Stack>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ minWidth: 0 }}>
                                  <Button size="small" variant="outlined" sx={{ minWidth: 64 }} onClick={(event) => { event.stopPropagation(); openEditClient(client); }}>{cmsText('Edit')}</Button>
                                  <Button size="small" variant="outlined" sx={{ minWidth: 64 }} onClick={(event) => { event.stopPropagation(); openAction('Add Personal Note', { clientId }); }}>{cmsText('Personal Notes')}</Button>
                                  <Button size="small" color="warning" sx={{ minWidth: 72 }} onClick={(event) => { event.stopPropagation(); archiveClient(clientId); }}>{cmsText('Archive')}</Button>
                                </Stack>
                              </Box>
                            );
                          })
                        ) : (
                          <Alert severity="info" action={<Button color="inherit" size="small" onClick={() => openAction('Add Client')}>{cmsText('Add Client')}</Button>}>
                            {cmsText('No clients found yet. Add your first real client to start filling the CMS dashboard.')}
                          </Alert>
                        )}
                      </Stack>
                      {activeSection === 'clients' && clientPagination.pages > 1 && (
                        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
                          <Pagination
                            count={clientPagination.pages}
                            page={clientPagination.page}
                            color="primary"
                            onChange={(_, page) => setClientPage(page)}
                          />
                        </Stack>
                      )}
                    </Paper>
                  </Box>
                </Box>
                )}

                {showModuleRecords && (
                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{cmsText(active.label)} {cmsText('Records')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {cmsText('Showing saved records connected to client records.')}
                        </Typography>
                      </Box>
                      {moduleAction && (
                        <Button variant="contained" startIcon={<Add />} onClick={() => openAction(moduleAction, primaryActionForm)}>
                          {cmsText(moduleActionLabel)}
                        </Button>
                      )}
                    </Stack>

                    <Stack spacing={1.25}>
                      {moduleRecords.length ? (
                        moduleRecords.map((record) => (
                          <Box
                            key={`${record.clientId}-${record.id || record._id}`}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'minmax(0, 1fr) auto',
                              gap: 1,
                              p: 1.5,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              bgcolor: 'background.paper',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(110px, max-content) minmax(0, 1fr)', sm: 'minmax(180px, max-content) minmax(0, 1fr)' }, gap: 1.25, alignItems: 'center', cursor: 'pointer' }} onClick={() => setProfileDetail({ kind: 'record', label: active.label, record })}>
                              <Typography sx={{ fontWeight: 850 }} noWrap>{record.title || record.typeLabel || cmsText('Record')}</Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>{getRecordMeta(record) || record.clientName || getRecordNote(record) || '-'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                              <IconButton size="small" color="primary" aria-label={cmsText('View')} onClick={() => setProfileDetail({ kind: 'record', label: active.label, record })}>
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="warning" aria-label={cmsText('Delete')} onClick={() => deleteRecord(record)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </Box>
                        ))
                      ) : (
                        <Alert severity="info" action={moduleAction ? <Button color="inherit" size="small" onClick={() => openAction(moduleAction, primaryActionForm)}>{cmsText(moduleActionLabel)}</Button> : undefined}>
                          {cmsText('No records found for the current filters.')}
                        </Alert>
                      )}
                    </Stack>
                  </Paper>
                )}

                {activeSection === 'analytics' && (
                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{cmsText('Analytics')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, gap: 1.5 }}>
                      {stats.map(([label, value]) => (
                        <Paper key={label} elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="h5" sx={{ fontWeight: 850 }}>{value}</Typography>
                          <Typography variant="body2" color="text.secondary">{cmsText(String(label))}</Typography>
                        </Paper>
                      ))}
                    </Box>
                  </Paper>
                )}

                {activeSection === 'templates' && (
                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{cmsText('Templates')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                      {templateActions.map((template) => (
                        <ButtonBase
                          key={template.title}
                          onClick={() => openAction(template.action, template.form)}
                          sx={{ display: 'block', width: '100%', textAlign: 'left', borderRadius: 1.5 }}
                        >
                          <Paper
                            elevation={0}
                            sx={{
                              width: '100%',
                              p: 2,
                              borderRadius: 1.5,
                              border: '1px solid',
                              borderColor: 'divider',
                              transition: 'border-color 0.15s ease, background-color 0.15s ease',
                              '&:hover': {
                                borderColor: 'primary.light',
                                bgcolor: 'rgba(46, 125, 82, 0.04)',
                              },
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>{cmsText(template.title)}</Typography>
                            <Typography variant="body2" color="text.secondary">{cmsText(template.helper)}</Typography>
                          </Paper>
                        </ButtonBase>
                      ))}
                    </Box>
                  </Paper>
                )}

                {activeSection === 'settings' && (
                  <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{cmsText('Settings')}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
                      {[
                        ['Statuses', clientStatuses],
                        ['Topics', clientTopics],
                        ['Sources', clientSources],
                      ].map(([label, values]) => (
                        <Paper key={label as string} elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography sx={{ fontWeight: 800, mb: 1 }}>{cmsText(label as string)}</Typography>
                          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                            {(values as string[]).map((value) => <Chip key={value} size="small" variant="outlined" label={cmsText(value)} />)}
                          </Stack>
                        </Paper>
                      ))}
                    </Box>
                  </Paper>
                )}

              </>
            )}
              </>
            )}
          </Stack>
        </Stack>
      </Container>

      <Dialog open={Boolean(dialogAction)} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText(action?.title || 'CMS Action')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {action?.needsClient && (
              <Autocomplete
                options={clientOptions}
                value={selectedClient}
                inputValue={clientSearch}
                loading={clientSearchLoading}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') setClientSearch(value);
                  if (reason === 'clear') setClientSearch('');
                }}
                onChange={(_event, value) => {
                  setField('clientId', value?._id || value?.id || '');
                  setClientSearch(value ? getClientOptionLabel(value) : '');
                }}
                getOptionLabel={getClientOptionLabel}
                isOptionEqualToValue={(option, value) => (option._id || option.id) === (value._id || value.id)}
                filterOptions={(options) => options}
                noOptionsText={clientSearch ? cmsText('No clients found') : cmsText('Type to search clients')}
                renderOption={(props, option, { selected }) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      color: 'text.primary',
                      bgcolor: selected ? 'rgba(46, 125, 82, 0.10)' : 'background.paper',
                      '&.Mui-focused': {
                        bgcolor: 'rgba(46, 125, 82, 0.08)',
                      },
                      '&[aria-selected="true"]': {
                        bgcolor: 'rgba(46, 125, 82, 0.12)',
                        color: 'text.primary',
                      },
                    }}
                  >
                    {getClientOptionLabel(option)}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={cmsText('Client')}
                    required
                    helperText={cmsText('Search by name, phone, email, topic, or tag')}
                  />
                )}
              />
            )}

            {action?.type === 'charts' && (
              <Autocomplete
                multiple
                options={chartOptions}
                value={selectedCharts}
                inputValue={chartSearch}
                loading={chartSearchLoading}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') setChartSearch(value);
                  if (reason === 'clear') setChartSearch('');
                }}
                onChange={(_event, value) => {
                  setSelectedCharts(value);
                  setChartSearch('');
                }}
                getOptionLabel={getChartOptionLabel}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                filterOptions={(options) => options}
                noOptionsText={chartSearch ? cmsText('No charts found') : cmsText('Type to search saved charts')}
                renderOption={(props, option, { selected }) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      color: 'text.primary',
                      bgcolor: selected ? 'rgba(46, 125, 82, 0.10)' : 'background.paper',
                      '&.Mui-focused': {
                        bgcolor: 'rgba(46, 125, 82, 0.08)',
                      },
                      '&[aria-selected="true"]': {
                        bgcolor: 'rgba(46, 125, 82, 0.12)',
                        color: 'text.primary',
                      },
                    }}
                  >
                    {getChartOptionLabel(option)}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={cmsText('Charts')}
                    required
                    helperText={cmsText('Search your saved charts by chart name')}
                  />
                )}
              />
            )}

            {action?.type === 'relations' && (
              <Autocomplete
                options={relatedClientOptions}
                value={selectedRelatedClient}
                inputValue={relatedClientSearch}
                loading={relatedClientSearchLoading}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') setRelatedClientSearch(value);
                  if (reason === 'clear') setRelatedClientSearch('');
                }}
                onChange={(_event, value) => {
                  setField('relatedClientId', value?._id || value?.id || '');
                  setField('relatedClientName', value?.fullName || '');
                  setRelatedClientSearch(value ? getClientOptionLabel(value) : '');
                }}
                getOptionLabel={getClientOptionLabel}
                isOptionEqualToValue={(option, value) => (option._id || option.id) === (value._id || value.id)}
                filterOptions={(options) => options.filter((option) => (option._id || option.id) !== form.clientId)}
                noOptionsText={relatedClientSearch ? cmsText('No clients found') : cmsText('Type to search related client')}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={cmsText('Related Client')}
                    required
                    helperText={cmsText('Connect this client with another saved client profile')}
                  />
                )}
              />
            )}

            {action?.type === 'client' ? (
              <>
                <TextField label={cmsText('Full Name')} value={form.fullName || ''} onChange={(event) => setField('fullName', event.target.value)} required autoFocus />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label={cmsText('Phone Number')} value={form.phoneNumber || ''} onChange={(event) => setField('phoneNumber', event.target.value)} fullWidth />
                  <TextField label={cmsText('WhatsApp Number')} value={form.whatsappNumber || ''} onChange={(event) => setField('whatsappNumber', event.target.value)} fullWidth />
                </Stack>
                <TextField label={cmsText('Email')} value={form.email || ''} onChange={(event) => setField('email', event.target.value)} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label={cmsText('Birth Place')} value={form.birthPlace || ''} onChange={(event) => setField('birthPlace', event.target.value)} fullWidth />
                  <TextField label={cmsText('Current City')} value={form.currentCity || ''} onChange={(event) => setField('currentCity', event.target.value)} fullWidth />
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label={cmsText('Main Topic')} value={form.mainTopic || ''} onChange={(event) => setField('mainTopic', event.target.value)} fullWidth>
                    {clientTopics.map((topic) => <MenuItem key={topic} value={topic}>{cmsText(topic)}</MenuItem>)}
                  </TextField>
                  <TextField select label={cmsText('Status')} value={form.clientStatus || 'New'} onChange={(event) => setField('clientStatus', event.target.value)} fullWidth>
                    {clientStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField select label={cmsText('Source')} value={form.clientSource || ''} onChange={(event) => setField('clientSource', event.target.value)}>
                  {clientSources.map((source) => <MenuItem key={source} value={source}>{cmsText(source)}</MenuItem>)}
                </TextField>
                <TextField label={cmsText('Tags')} helperText={cmsText('Comma separated')} value={form.tags || ''} onChange={(event) => setField('tags', event.target.value)} />
                <TextField label={cmsText('Internal Note')} value={form.internalNote || ''} onChange={(event) => setField('internalNote', event.target.value)} multiline minRows={3} />
              </>
            ) : action?.type === 'charts' ? (
              <>
                <TextField label={cmsText('Purpose')} value={form.topic || ''} onChange={(event) => setField('topic', event.target.value)} placeholder={cmsText('Client Chart')} />
                <TextField label={cmsText('Tags')} helperText={cmsText('Comma separated')} value={form.tags || ''} onChange={(event) => setField('tags', event.target.value)} />
                <TextField label={cmsText('Notes')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={3} />
              </>
            ) : action?.type === 'relations' ? (
              <>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField select label={cmsText('Relation Type')} value={form.relationType || ''} onChange={(event) => setField('relationType', event.target.value)} required fullWidth>
                    {relationTypes.map((relationType) => <MenuItem key={relationType} value={relationType}>{cmsText(relationType)}</MenuItem>)}
                  </TextField>
                  <TextField select label={cmsText('Status')} value={form.status || 'Active'} onChange={(event) => setField('status', event.target.value)} fullWidth>
                    {relationStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
                  </TextField>
                </Stack>
                <TextField select label={cmsText('Linked Chart')} value={form.linkedChartId || ''} onChange={(event) => setField('linkedChartId', event.target.value)} required helperText={!linkedChartOptions.length ? cmsText('Link a chart to this client before adding records.') : cmsText('Select linked chart')}>
                  <MenuItem value="">{cmsText('Select linked chart')}</MenuItem>
                  {linkedChartOptions.map((chart) => (
                    <MenuItem key={chart._id || chart.id || chart.chartId} value={chart.chartId || ''}>
                      {[chart.chartName || cmsText('Client Chart'), chart.purpose, chart.chartType].filter(Boolean).join(' - ')}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField label={cmsText('Notes')} value={form.note || ''} onChange={(event) => setField('note', event.target.value)} multiline minRows={3} />
              </>
            ) : (
              renderRecordDialogFields()
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving}>{cmsText('Cancel')}</Button>
          <Button onClick={submitDialog} variant="contained" disabled={saving || (action?.type === 'client' && !form.fullName) || (action?.needsClient && !form.clientId) || (recordRequiresChart && !form.linkedChartId) || (action?.type === 'charts' && !selectedCharts.length) || (action?.type === 'relations' && (!form.relatedClientId || !form.relationType))}>
            {saving ? cmsText('Saving') : cmsText('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingClient)} onClose={closeEditClient} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText('Edit Client')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label={cmsText('Full Name')} value={form.fullName || ''} onChange={(event) => setField('fullName', event.target.value)} required autoFocus />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={cmsText('Phone Number')} value={form.phoneNumber || ''} onChange={(event) => setField('phoneNumber', event.target.value)} fullWidth />
              <TextField label={cmsText('WhatsApp Number')} value={form.whatsappNumber || ''} onChange={(event) => setField('whatsappNumber', event.target.value)} fullWidth />
            </Stack>
            <TextField label={cmsText('Email')} value={form.email || ''} onChange={(event) => setField('email', event.target.value)} />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label={cmsText('Birth Place')} value={form.birthPlace || ''} onChange={(event) => setField('birthPlace', event.target.value)} fullWidth />
              <TextField label={cmsText('Current City')} value={form.currentCity || ''} onChange={(event) => setField('currentCity', event.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label={cmsText('Main Topic')} value={form.mainTopic || ''} onChange={(event) => setField('mainTopic', event.target.value)} fullWidth>
                <MenuItem value="">{cmsText('No Topic')}</MenuItem>
                {clientTopics.map((topic) => <MenuItem key={topic} value={topic}>{cmsText(topic)}</MenuItem>)}
              </TextField>
              <TextField select label={cmsText('Status')} value={form.clientStatus || 'New'} onChange={(event) => setField('clientStatus', event.target.value)} fullWidth>
                {clientStatuses.map((status) => <MenuItem key={status} value={status}>{cmsText(status)}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField select label={cmsText('Source')} value={form.clientSource || ''} onChange={(event) => setField('clientSource', event.target.value)}>
              <MenuItem value="">{cmsText('No Source')}</MenuItem>
              {clientSources.map((source) => <MenuItem key={source} value={source}>{cmsText(source)}</MenuItem>)}
            </TextField>
            <TextField label={cmsText('Tags')} helperText={cmsText('Comma separated')} value={form.tags || ''} onChange={(event) => setField('tags', event.target.value)} />
            <TextField label={cmsText('Internal Note')} value={form.internalNote || ''} onChange={(event) => setField('internalNote', event.target.value)} multiline minRows={3} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditClient} disabled={saving}>{cmsText('Cancel')}</Button>
          <Button onClick={saveEditedClient} variant="contained" disabled={saving || !form.fullName}>
            {saving ? cmsText('Saving') : cmsText('Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(profileDetail)} onClose={() => setProfileDetail(null)} fullWidth maxWidth="sm">
        <DialogTitle>{cmsText(profileDetail?.label || 'Record')}</DialogTitle>
        <DialogContent dividers>
          {profileDetail && (
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 850 }}>
                {getRecordTitle(profileDetail.record, profileDetail.label)}
              </Typography>
              {getDetailRows(profileDetail).map(([label, value]) => (
                <Box key={`${label}-${value}`} sx={{ display: 'grid', gridTemplateColumns: '140px minmax(0, 1fr)', gap: 2, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>{cmsText(String(label))}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>{cmsText(String(value))}</Typography>
                </Box>
              ))}
              {getRecordNote(profileDetail.record) && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {getRecordNote(profileDetail.record)}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {profileDetail?.kind === 'record' && (
            <Button
              variant="outlined"
              onClick={() => {
                const record = profileDetail.record as CmsModuleRecord;
                setProfileDetail(null);
                openEditRecord(record);
              }}
            >
              {cmsText('Edit')}
            </Button>
          )}
          {profileDetail?.kind === 'relation' && (
            <Button
              variant="outlined"
              onClick={() => {
                const relation = profileDetail.record as CmsRelation;
                setProfileDetail(null);
                openEditRelation(relation);
              }}
            >
              {cmsText('Edit')}
            </Button>
          )}
          <Button onClick={() => setProfileDetail(null)} variant="contained">{cmsText('Close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientManagement;
